import React from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Seo } from '../components/SEO';

const MainTitle = styled.h1`
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 48px;
  padding-bottom: 24px;
  border-bottom: 2px solid #333;
`;

// Updated ImageSection to maintain 16:9 ratio
const ImageSection = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  background-color: #f3f3f3;

  @media (max-width: 1023px) {
    padding-top: 56.25%;
  }

  @media (min-width: 1024px) {
    width: 40%;
    aspect-ratio: 16/9;
  }
`;

const VideoWrapper = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// Updated LinkButton to have black text
const LinkButton = styled.a`
  text-transform: uppercase;
  font-size: 14px;
  border: 1px solid #333;
  padding: 8px 16px;
  transition: all 0.3s ease;
  color: #000;
  text-decoration: none;

  &:hover {
    background-color: #333;
    color: white;
  }
`;

// Rest of the styled components remain the same...
const PageContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 48px 16px;
`;

const CategorySection = styled.div`
  margin-bottom: 64px;
`;

const CategoryTitle = styled.h2`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #333;
`;

const PublicationContainer = styled.div`
  margin-bottom: 48px;
  border-bottom: 1px solid #333;
  padding-bottom: 32px;
  &:last-child {
    border-bottom: none;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const ContentSection = styled.div`
  width: 100%;

  @media (min-width: 1024px) {
    width: 60%;
  }
`;

const ConferenceName = styled.div`
  color: #666;
  margin-bottom: 8px;

  .to-appear {
    font-style: italic;
    margin-left: 8px;
    color: red;
  }
`;

const PaperTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const AuthorList = styled.div`
  margin-bottom: 24px;
`;

const Award = styled.div`
  color: #2ecc71;
  margin-bottom: 16px;
`;

const LinkContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const Research = () => {
  const data = useStaticQuery(graphql`
    query {
      allMdx(
        sort: { frontmatter: { date: DESC } }
        filter: { internal: { contentFilePath: { regex: "/publications/" } } }
      ) {
        group(field: { frontmatter: { type: SELECT } }) {
          fieldValue
          nodes {
            frontmatter {
              title
              conference
              authors
              award
              links {
                project
                video
                github
                additional {
                  label
                  url
                }
              }
              date
              featured
              toAppear
            }
            parent {
              ... on File {
                relativeDirectory
              }
            }
          }
        }
      }
      images: allFile(
        filter: {
          sourceInstanceName: { eq: "publications" }
          extension: { in: ["jpg", "jpeg", "png", "webp", "gif"] }
        }
      ) {
        nodes {
          relativePath
          publicURL
          name
          extension
          relativeDirectory
          childImageSharp {
            gatsbyImageData(
              width: 800
              placeholder: BLURRED
              formats: [AUTO, WEBP]
              quality: 90
            )
          }
        }
      }
      videos: allFile(
        filter: {
          sourceInstanceName: { eq: "publications" }
          extension: { in: ["mp4", "webm", "pdf"] }
        }
      ) {
        nodes {
          relativePath
          publicURL
          name
          extension
          relativeDirectory
        }
      }
    }
  `);

  const getPublicationFiles = (directory) => {
    const imageFiles = data.images.nodes.filter(
      (file) => file.relativeDirectory === directory
    );
    const videoFiles = data.videos.nodes.filter(
      (file) => file.relativeDirectory === directory
    );

    // Updated to match actual file names
    const coverVideo = videoFiles.find(
      (file) => file.name === 'cover' && file.extension === 'mp4'
    );

    const coverImage = imageFiles.find(
      (file) =>
        file.name === 'cover' &&
        (file.extension === 'png' || file.extension === 'jpg')
    );

    const paperPDF = videoFiles.find(
      (file) => file.name === 'paper' && file.extension === 'pdf'
    );

    return {
      coverVideo: coverVideo?.publicURL,
      coverImage: coverImage?.childImageSharp?.gatsbyImageData,
      paperPDF: paperPDF?.publicURL,
    };
  };

  const getToAppearFiles = (directory) => {
    const files = data.images.nodes.filter(
      (file) => file.relativeDirectory === directory
    );

    // Try to find any image in the directory if "to-appear" isn't found
    const toAppearImage = files.find(
      (file) =>
        file.name === 'to-appear' &&
        (file.extension === 'png' ||
          file.extension === 'jpg' ||
          file.extension === 'jpeg')
    );

    // Fallback to any image in the directory
    const anyImage = files.find(
      (file) =>
        file.extension === 'png' ||
        file.extension === 'jpg' ||
        file.extension === 'jpeg'
    );

    return {
      coverVideo: null,
      coverImage:
        toAppearImage?.childImageSharp?.gatsbyImageData ||
        anyImage?.childImageSharp?.gatsbyImageData,
      paperPDF: null,
    };
  };

  const PublicationCard = ({ publication }) => {
    const {
      frontmatter: {
        title,
        conference,
        authors,
        award,
        links,
        toAppear = false,
      },
      parent: { relativeDirectory },
    } = publication;

    // Get files differently based on publication status
    const files = toAppear
      ? getToAppearFiles(relativeDirectory)
      : getPublicationFiles(relativeDirectory);

    const { coverVideo, coverImage, paperPDF } = files;

    return (
      <PublicationContainer>
        <FlexContainer>
          <ImageSection>
            {toAppear ? (
              // Display placeholder image for upcoming publications
              <div className="to-appear-container">
                {coverImage ? (
                  <GatsbyImage
                    image={coverImage}
                    alt={`${title} - To Appear`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                ) : (
                  // Fallback if no image is found
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#f3f3f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ color: '#666', fontStyle: 'italic' }}>
                      To Appear
                    </span>
                  </div>
                )}
              </div>
            ) : (
              // Display regular content for published papers
              <>
                {coverVideo ? (
                  <VideoWrapper
                    autoPlay
                    loop
                    muted
                    playsInline
                    src={coverVideo}
                  />
                ) : (
                  coverImage && (
                    <GatsbyImage
                      image={coverImage}
                      alt={title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  )
                )}
              </>
            )}
          </ImageSection>

          <ContentSection>
            <ConferenceName>
              {conference}
              {toAppear && <span className="to-appear">to appear</span>}
            </ConferenceName>
            <PaperTitle>{title}</PaperTitle>
            <AuthorList>{authors.join(', ')}</AuthorList>

            {award && <Award>{award}</Award>}

            {/* Only show links section if not a "to appear" paper or if it has specific links */}
            {(!toAppear || (links && Object.values(links).some((v) => v))) && (
              <LinkContainer>
                {paperPDF && (
                  <LinkButton
                    href={paperPDF}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    PDF
                  </LinkButton>
                )}
                {Object.entries(links || {}).map(([key, value]) => {
                  if (!value || key === 'additional') return null;

                  return (
                    <LinkButton
                      key={key}
                      href={value}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {key}
                    </LinkButton>
                  );
                })}
                {links?.additional?.map(({ label, url }) => (
                  <LinkButton
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label}
                  </LinkButton>
                ))}
              </LinkContainer>
            )}
          </ContentSection>
        </FlexContainer>
      </PublicationContainer>
    );
  };

  return (
    <PageContainer>
      <MainTitle>Research Papers</MainTitle>
      {data.allMdx.group.map(({ fieldValue: category, nodes }) => (
        <CategorySection key={category}>
          <CategoryTitle>{category}</CategoryTitle>
          {nodes.map((publication, index) => (
            <PublicationCard key={index} publication={publication} />
          ))}
        </CategorySection>
      ))}
    </PageContainer>
  );
};

export default Research;

export const Head = () => (
  <Seo
    title="Research & Publications - Jesse Gao"
    description="Browse Jesse's research publications in HCI, including papers at CHI, UIST, and other top venues."
    pathname="/research/"
  />
);
